import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { useDisclosure } from '@chakra-ui/react';
import { LoadingIndicator } from '@/components/v4';
import { CustomSIPHeaderFormProvider } from './context/CustomSIPHeaderFormContext.jsx';
import { useCustomSIPHeaderForm } from './hooks/useCustomSIPHeaderForm.js';
import useGetCustomSIPHeaderByID from './queries/useGetCustomSIPHeaderByID.jsx';
import useCreateCustomSIPHeader from './queries/useCreateCustomSIPHeader.jsx';
import useUpdateCustomSIPHeader from './queries/useUpdateCustomSIPHeader.jsx';
import { getInitialFormData } from './utils/customSIPHeaderFormUtils.js';
import CustomSIPHeaderName from './formFields/CustomSIPHeaderName';
import CustomSIPHeaderSuffix from './formFields/CustomSIPHeaderSuffix';
import CustomSIPHeaderType from './formFields/CustomSIPHeaderType';
import CustomSIPHeaderLinkObjectName from './formFields/CustomSIPHeaderLinkObjectName';
import CustomSIPHeaderValue from './formFields/CustomSIPHeaderValue';
import FormBase from '@/components/forms/new/FormBase.jsx';
import { customSIPHeaderValidationSchema } from './validation/customSIPHeaderValidation.js';
import CustomSIPHeaderDeleteModal from './modals/CustomSIPHeaderDeleteModal.jsx';

const CustomSIPHeaderEditContent = ({
    initialData,
    mode,
    resetToMainTable,
    selectedHeaderID,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    const {
        isOpen: isDeleteOpen,
        onOpen: onDeleteOpen,
        onClose: onDeleteClose,
    } = useDisclosure();

    const { formData, setFormData, initialFormData, setInitialFormData } =
        useCustomSIPHeaderForm();

    const isAddMode = mode === 'Add';
    const isEditMode = mode === 'Edit';
    const hasInitialized = useRef(false);

    // Initialize form data only once when component mounts in Add mode
    useEffect(() => {
        if (isAddMode && !hasInitialized.current) {
            setFormData(getInitialFormData(initialData));
            setInitialFormData(getInitialFormData(initialData));
            hasInitialized.current = true;
        }
    }, [isAddMode, setFormData, initialData, setInitialFormData]);

    const { isLoading, isFetching } = useGetCustomSIPHeaderByID(
        selectedHeaderID,
        setFormData,
        setInitialFormData,
    );

    const getInitialValues = () => {
        return formData;
    };

    const isSubmitDisabled = useMemo(() => {
        if (isAddMode) {
            const hasRequiredFields = !!(
                formData.name &&
                formData.suffix &&
                formData.customHeaderTypeID &&
                formData.linkID &&
                formData.value
            );
            return !hasRequiredFields;
        }

        if (!initialFormData || !formData) return true;
        const hasChanged = !isEqual(initialFormData, formData);
        return !hasChanged;
    }, [formData, initialFormData, isAddMode]);

    const breadCrumbTrail = [
        { label: 'Custom SIP Headers', path: resetToMainTable },
        {
            label: isAddMode ? 'Add Header' : 'Edit Header',
            path: '',
        },
    ];

    const header = isAddMode
        ? 'Add Custom SIP Header'
        : `Edit ${initialFormData?.name || 'Header'}`;

    const { mutate: createHeader } = useCreateCustomSIPHeader(
        formData,
        resetToMainTable,
    );

    const { mutate: updateHeader } = useUpdateCustomSIPHeader(
        formData,
        resetToMainTable,
    );

    const finalFormSubmit = () => {
        if (isAddMode) {
            createHeader();
        } else {
            updateHeader();
        }
    };

    const formFields = (errors, touched, values) => {
        return [
            <CustomSIPHeaderName
                key="name"
                darkMode={darkMode}
                errors={errors}
                touched={touched}
            />,
            <CustomSIPHeaderSuffix
                key="suffix"
                darkMode={darkMode}
                errors={errors}
                touched={touched}
            />,
            <CustomSIPHeaderType
                key="type"
                darkMode={darkMode}
                errors={errors}
                touched={touched}
            />,
            <CustomSIPHeaderLinkObjectName
                key="linkObjectName"
                darkMode={darkMode}
                errors={errors}
                touched={touched}
            />,
            <CustomSIPHeaderValue
                key="value"
                darkMode={darkMode}
                errors={errors}
                touched={touched}
                values={values}
            />,
        ];
    };

    if ((isLoading || isFetching) && isEditMode) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={getInitialValues()}
                validationSchema={customSIPHeaderValidationSchema}
                finalFormSubmit={finalFormSubmit}
                backLink={resetToMainTable}
                submitButtonText={isAddMode ? 'Create' : 'Save'}
                deleteButton={
                    isEditMode && {
                        isLoading: isLoading,
                        onDeleteOpen: onDeleteOpen,
                    }
                }
                isSubmitDisabled={isSubmitDisabled}
                enableReinitialize={true}>
                {({ errors, touched, values }) =>
                    formFields(errors, touched, values)
                }
            </FormBase>
            <CustomSIPHeaderDeleteModal
                isOpen={isDeleteOpen}
                onClose={onDeleteClose}
                headerData={formData}
                resetToMainTable={resetToMainTable}
            />
        </>
    );
};

const CustomSIPHeaderEdit = ({ mode, resetToMainTable, selectedHeaderID }) => {
    const { currentPartner } = useSelector((state) => state.navigationLists);

    const initialData = {
        partnerID: currentPartner,
    };

    return (
        <CustomSIPHeaderFormProvider initialData={initialData}>
            <CustomSIPHeaderEditContent
                mode={mode}
                resetToMainTable={resetToMainTable}
                selectedHeaderID={selectedHeaderID}
                initialData={initialData}
            />
        </CustomSIPHeaderFormProvider>
    );
};

export default CustomSIPHeaderEdit;
